import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { login as loginRequest } from '../../services/authService';
import { getStudent } from '../../services/studentService';
import {
  getPipelineStatus,
  getPipelineTraces,
  getPipelineAudit,
  clearPipelineRecords,
} from '../../services/monitoringService';

const REQUIRED_PIN = '2003';
const REFRESH_MS = 5000;

function formatClockTime(value) {
  if (!value) return '—';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString('en-GB', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatDurationMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const totalSeconds = Math.floor(Math.max(0, ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((v) => String(v).padStart(2, '0'))
    .join(':');
}

const FILE_STATUS_PRIORITY = {
  error: 5,
  timeout: 4,
  non_compliant: 3,
  needs_attention: 2,
  insufficient_evidence: 2,
  compliant: 1,
};

function deriveWorstStatus(rows) {
  let worst = 'compliant';
  let worstRank = 0;
  for (const r of rows) {
    const st = r.status || 'unknown';
    const rank = FILE_STATUS_PRIORITY[st] ?? 1;
    if (rank > worstRank) {
      worstRank = rank;
      worst = st;
    }
  }
  return worst;
}

function emptyMetrics() {
  return {
    latestAt: null,
    numChunks: 0,
    rerankScore: null,
    promptTokens: 0,
    completionTokens: 0,
    totalTokens: 0,
    llmCalls: 0,
    llmMs: 0,
    elapsedMs: 0,
    rowCount: 0,
  };
}

function applyRowMetrics(target, r) {
  if (r.created_at && (!target.latestAt || r.created_at > target.latestAt)) {
    target.latestAt = r.created_at;
  }
  target.numChunks += r.num_chunks || 0;
  if (r.rerank_score != null) {
    target.rerankScore = target.rerankScore == null
      ? r.rerank_score
      : Math.max(target.rerankScore, r.rerank_score);
  }
  target.promptTokens += r.prompt_tokens || 0;
  target.completionTokens += r.completion_tokens || 0;
  target.totalTokens += r.total_tokens || 0;
  target.llmCalls += r.llm_calls || 0;
  target.llmMs += r.llm_ms || 0;
  target.elapsedMs += r.elapsed_ms || 0;
  target.rowCount += 1;
}

function groupAuditHierarchy(rows) {
  const studentMap = new Map();
  for (const r of rows) {
    const studentId = r.student_id ?? null;
    const studentKey = studentId != null
      ? `student:${studentId}`
      : r.job_id
        ? `job:${r.job_id}`
        : 'unknown';

    if (!studentMap.has(studentKey)) {
      studentMap.set(studentKey, {
        key: studentKey,
        studentId,
        jobId: r.job_id ?? null,
        files: new Map(),
        rows: [],
        ...emptyMetrics(),
      });
    }
    const student = studentMap.get(studentKey);
    student.rows.push(r);
    applyRowMetrics(student, r);

    const fileKey = r.filename || r.document_id || 'unknown';
    if (!student.files.has(fileKey)) {
      student.files.set(fileKey, {
        key: fileKey,
        filename: r.filename || '—',
        documentId: r.document_id,
        sections: [],
        ...emptyMetrics(),
      });
    }
    const file = student.files.get(fileKey);
    file.sections.push(r);
    applyRowMetrics(file, r);
  }

  return Array.from(studentMap.values())
    .map((student) => ({
      ...student,
      status: deriveWorstStatus(student.rows),
      files: Array.from(student.files.values())
        .map((file) => ({
          ...file,
          status: deriveWorstStatus(file.sections),
          sections: [...file.sections].sort(
            (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
          ),
        }))
        .sort((a, b) => new Date(b.latestAt || 0) - new Date(a.latestAt || 0)),
    }))
    .sort((a, b) => new Date(b.latestAt || 0) - new Date(a.latestAt || 0));
}

function studentLabel(student, labels) {
  if (student.studentId != null) {
    return labels[student.studentId] || `Student #${student.studentId}`;
  }
  if (student.jobId) {
    return `Batch ${String(student.jobId).slice(0, 8)}`;
  }
  return 'Unassigned';
}

function statusColor(st) {
  if (st === 'compliant') return 'text-emerald-400';
  if (st === 'non_compliant' || st === 'error' || st === 'timeout') return 'text-red-400';
  if (st === 'insufficient_evidence') return 'text-amber-400';
  return 'text-slate-300';
}

function Chevron({ open }) {
  return (
    <span className={`inline-block transition-transform text-slate-500 ${open ? 'rotate-90' : ''}`}>
      ▸
    </span>
  );
}

function AuditMetricCells({ row }) {
  return (
    <>
      <td className={`px-2 sm:px-3 py-2 ${statusColor(row.status)}`}>{row.status || '—'}</td>
      <td className="px-2 sm:px-3 py-2 text-right">{row.numChunks ?? '—'}</td>
      <td className="px-2 sm:px-3 py-2 text-right hidden lg:table-cell">
        {row.rerankScore != null ? row.rerankScore.toFixed(2) : '—'}
      </td>
      <td className="px-2 sm:px-3 py-2 text-right">{row.promptTokens ?? 0}</td>
      <td className="px-2 sm:px-3 py-2 text-right hidden sm:table-cell">{row.completionTokens ?? 0}</td>
      <td className="px-2 sm:px-3 py-2 text-right hidden md:table-cell">{row.llmCalls ?? 0}</td>
      <td className="px-2 sm:px-3 py-2 text-right tabular-nums hidden lg:table-cell">
        {formatDurationMs(row.llmMs)}
      </td>
      <td className="px-2 sm:px-3 py-2 text-right tabular-nums">{formatDurationMs(row.elapsedMs)}</td>
    </>
  );
}

function MetricsPills({ row }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2 text-[11px] text-slate-400">
      <span className="px-1.5 py-0.5 rounded bg-slate-800">{row.promptTokens ?? 0} in</span>
      <span className="px-1.5 py-0.5 rounded bg-slate-800">{row.completionTokens ?? 0} out</span>
      <span className="px-1.5 py-0.5 rounded bg-slate-800">{row.llmCalls ?? 0} calls</span>
      <span className="px-1.5 py-0.5 rounded bg-slate-800">{formatDurationMs(row.elapsedMs)}</span>
    </div>
  );
}

function ConfirmClearModal({ open, busy, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl p-5 shadow-xl">
        <h2 className="text-base font-semibold text-slate-100">Clear pipeline records?</h2>
        <p className="text-sm text-slate-400 mt-2">
          This removes all live trace buffer entries and persistent section audit rows from the
          monitor. Running pipelines are not stopped.
        </p>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm bg-slate-800 border border-slate-700 text-slate-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
          >
            {busy ? 'Clearing…' : 'Clear records'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- PIN Gate ------------------------------- */
function BackdoorGate({ onUnlock }) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pin !== REQUIRED_PIN) {
      setError('Invalid access PIN.');
      return;
    }
    if (user?.email && email.trim().toLowerCase() !== String(user.email).toLowerCase()) {
      setError('Email must match the signed-in user.');
      return;
    }
    setBusy(true);
    try {
      await loginRequest(email.trim(), password);
      sessionStorage.setItem('backdoor_ok', '1');
      onUnlock();
    } catch {
      setError('Invalid credentials.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4"
      >
        <div className="text-center">
          <h1 className="text-lg font-semibold text-slate-100">Internal Monitor</h1>
          <p className="text-xs text-slate-500 mt-1">Restricted — authorized staff only</p>
        </div>
        <input
          type="email" placeholder="Email" value={email} autoComplete="off"
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 focus:outline-none focus:border-indigo-500"
        />
        <input
          type="password" placeholder="Password" value={password} autoComplete="off"
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 focus:outline-none focus:border-indigo-500"
        />
        <input
          type="password" placeholder="Access PIN" value={pin} autoComplete="off"
          onChange={(e) => setPin(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 focus:outline-none focus:border-indigo-500"
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="submit" disabled={busy}
          className="w-full py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium disabled:opacity-50"
        >
          {busy ? 'Verifying…' : 'Enter'}
        </button>
      </form>
    </div>
  );
}

/* ------------------------------ Stat card ------------------------------ */
function Stat({ label, value, sub, tone = 'slate' }) {
  const toneMap = {
    slate: 'text-slate-100', green: 'text-emerald-400',
    amber: 'text-amber-400', red: 'text-red-400', indigo: 'text-indigo-400',
  };
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 sm:p-4">
      <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl sm:text-2xl font-semibold mt-1 ${toneMap[tone]}`}>{value}</div>
      {sub != null && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function AuditTableDesktop({
  studentGroups,
  studentLabels,
  expandedStudents,
  expandedFiles,
  onToggleStudent,
  onToggleFile,
}) {
  return (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="text-xs uppercase text-slate-500 bg-slate-900/60">
          <tr>
            <th className="w-8 px-2 py-2" aria-label="Expand" />
            <th className="text-left px-2 sm:px-3 py-2">Time</th>
            <th className="text-left px-2 sm:px-3 py-2">Student / File / Section</th>
            <th className="text-left px-2 sm:px-3 py-2">Status</th>
            <th className="text-right px-2 sm:px-3 py-2">Chunks</th>
            <th className="text-right px-2 sm:px-3 py-2 hidden lg:table-cell">Rerank</th>
            <th className="text-right px-2 sm:px-3 py-2">In tok</th>
            <th className="text-right px-2 sm:px-3 py-2 hidden sm:table-cell">Out tok</th>
            <th className="text-right px-2 sm:px-3 py-2 hidden md:table-cell">Calls</th>
            <th className="text-right px-2 sm:px-3 py-2 hidden lg:table-cell">LLM</th>
            <th className="text-right px-2 sm:px-3 py-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {studentGroups.length === 0 && (
            <tr>
              <td colSpan={11} className="px-3 py-6 text-center text-slate-500">
                No audit rows yet — run a compliance job with tracing enabled.
              </td>
            </tr>
          )}
          {studentGroups.map((student) => {
            const studentOpen = !!expandedStudents[student.key];
            const label = studentLabel(student, studentLabels);
            return (
              <React.Fragment key={student.key}>
                <tr
                  className="border-t border-slate-800/70 hover:bg-slate-800/40 cursor-pointer bg-slate-900/30"
                  onClick={() => onToggleStudent(student.key)}
                >
                  <td className="px-2 py-2 text-center"><Chevron open={studentOpen} /></td>
                  <td className="px-2 sm:px-3 py-2 text-slate-400 whitespace-nowrap tabular-nums">
                    {formatClockTime(student.latestAt)}
                  </td>
                  <td className="px-2 sm:px-3 py-2 text-slate-100 font-semibold">
                    {label}
                    <span className="ml-2 text-xs font-normal text-slate-500">
                      ({student.files.length} file{student.files.length === 1 ? '' : 's'},
                      {' '}{student.rowCount} section{student.rowCount === 1 ? '' : 's'})
                    </span>
                  </td>
                  <AuditMetricCells row={student} />
                </tr>
                {studentOpen && student.files.map((file) => {
                  const fileKey = `${student.key}|${file.key}`;
                  const fileOpen = !!expandedFiles[fileKey];
                  return (
                    <React.Fragment key={fileKey}>
                      <tr
                        className="border-t border-slate-800/50 hover:bg-slate-800/30 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); onToggleFile(fileKey); }}
                      >
                        <td className="px-2 py-2 text-center pl-4"><Chevron open={fileOpen} /></td>
                        <td className="px-2 sm:px-3 py-2 text-slate-500 whitespace-nowrap tabular-nums">
                          {formatClockTime(file.latestAt)}
                        </td>
                        <td className="px-2 sm:px-3 py-2 text-slate-200 pl-6 max-w-[280px] truncate" title={file.filename}>
                          {file.filename}
                          <span className="ml-2 text-xs text-slate-500">
                            ({file.sections.length} section{file.sections.length === 1 ? '' : 's'})
                          </span>
                        </td>
                        <AuditMetricCells row={file} />
                      </tr>
                      {fileOpen && file.sections.map((r) => (
                        <tr key={r.id} className="border-t border-slate-800/40 bg-slate-950/40">
                          <td className="px-2 py-2" />
                          <td className="px-2 sm:px-3 py-2 text-slate-500 whitespace-nowrap tabular-nums pl-8">
                            {formatClockTime(r.created_at)}
                          </td>
                          <td className="px-2 sm:px-3 py-2 text-slate-400 pl-12">{r.section}</td>
                          <td className={`px-2 sm:px-3 py-2 ${statusColor(r.status)}`}>{r.status || '—'}</td>
                          <td className="px-2 sm:px-3 py-2 text-right">{r.num_chunks ?? '—'}</td>
                          <td className="px-2 sm:px-3 py-2 text-right hidden lg:table-cell">
                            {r.rerank_score != null ? r.rerank_score.toFixed(2) : '—'}
                          </td>
                          <td className="px-2 sm:px-3 py-2 text-right">{r.prompt_tokens ?? 0}</td>
                          <td className="px-2 sm:px-3 py-2 text-right hidden sm:table-cell">{r.completion_tokens ?? 0}</td>
                          <td className="px-2 sm:px-3 py-2 text-right hidden md:table-cell">{r.llm_calls ?? 0}</td>
                          <td className="px-2 sm:px-3 py-2 text-right tabular-nums hidden lg:table-cell">
                            {formatDurationMs(r.llm_ms)}
                          </td>
                          <td className="px-2 sm:px-3 py-2 text-right tabular-nums">{formatDurationMs(r.elapsed_ms)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AuditCardsMobile({
  studentGroups,
  studentLabels,
  expandedStudents,
  expandedFiles,
  onToggleStudent,
  onToggleFile,
}) {
  if (studentGroups.length === 0) {
    return (
      <div className="md:hidden px-4 py-6 text-center text-sm text-slate-500">
        No audit rows yet — run a compliance job with tracing enabled.
      </div>
    );
  }

  return (
    <div className="md:hidden divide-y divide-slate-800">
      {studentGroups.map((student) => {
        const studentOpen = !!expandedStudents[student.key];
        const label = studentLabel(student, studentLabels);
        return (
          <div key={student.key} className="p-3">
            <button
              type="button"
              className="w-full text-left"
              onClick={() => onToggleStudent(student.key)}
            >
              <div className="flex items-start gap-2">
                <Chevron open={studentOpen} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-100 truncate">{label}</span>
                    <span className={`text-xs shrink-0 ${statusColor(student.status)}`}>{student.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {formatClockTime(student.latestAt)} · {student.files.length} file(s) · {student.rowCount} section(s)
                  </div>
                  <MetricsPills row={student} />
                </div>
              </div>
            </button>
            {studentOpen && (
              <div className="mt-2 ml-5 space-y-2 border-l border-slate-800 pl-3">
                {student.files.map((file) => {
                  const fileKey = `${student.key}|${file.key}`;
                  const fileOpen = !!expandedFiles[fileKey];
                  return (
                    <div key={fileKey}>
                      <button
                        type="button"
                        className="w-full text-left"
                        onClick={() => onToggleFile(fileKey)}
                      >
                        <div className="flex items-start gap-2">
                          <Chevron open={fileOpen} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm text-slate-200 truncate" title={file.filename}>{file.filename}</span>
                              <span className={`text-xs shrink-0 ${statusColor(file.status)}`}>{file.status}</span>
                            </div>
                            <div className="text-xs text-slate-500">{file.sections.length} section(s)</div>
                            <MetricsPills row={file} />
                          </div>
                        </div>
                      </button>
                      {fileOpen && (
                        <div className="mt-2 ml-5 space-y-2">
                          {file.sections.map((r) => (
                            <div key={r.id} className="rounded-lg bg-slate-950/60 border border-slate-800 p-2.5">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs text-slate-300">{r.section}</span>
                                <span className={`text-xs ${statusColor(r.status)}`}>{r.status}</span>
                              </div>
                              <div className="text-[11px] text-slate-500 mt-1">{formatClockTime(r.created_at)}</div>
                              <MetricsPills row={{
                                promptTokens: r.prompt_tokens,
                                completionTokens: r.completion_tokens,
                                llmCalls: r.llm_calls,
                                elapsedMs: r.elapsed_ms,
                              }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------- Dashboard ------------------------------- */
function BackdoorDashboard() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState([]);
  const [traces, setTraces] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [err, setErr] = useState('');
  const [live, setLive] = useState(true);
  const [expandedStudents, setExpandedStudents] = useState({});
  const [expandedFiles, setExpandedFiles] = useState({});
  const [studentLabels, setStudentLabels] = useState({});
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, a, t] = await Promise.all([
        getPipelineStatus().catch(() => ({ data: null })),
        getPipelineAudit({ limit: 100 }).catch(() => ({ data: { audit: [] } })),
        getPipelineTraces(100).catch(() => ({ data: { traces: [] } })),
      ]);
      setStatus(s.data || null);
      setAudit(a.data?.audit || []);
      setTraces(t.data?.traces || []);
      setUpdatedAt(new Date());
      setErr('');
    } catch (e) {
      setErr('Failed to load monitoring data.');
    }
  }, []);

  useEffect(() => {
    load();
    if (live) {
      timer.current = setInterval(load, REFRESH_MS);
      return () => clearInterval(timer.current);
    }
    return undefined;
  }, [load, live]);

  useEffect(() => {
    const ids = [...new Set(audit.map((r) => r.student_id).filter((id) => id != null))];
    if (!ids.length) return undefined;

    let cancelled = false;
    Promise.all(
      ids.map((id) => getStudent(id)
        .then(({ data }) => [id, data?.data?.student_name || null])
        .catch(() => [id, null])),
    ).then((results) => {
      if (cancelled) return;
      setStudentLabels((prev) => {
        const next = { ...prev };
        for (const [id, name] of results) {
          if (name) next[id] = name;
        }
        return next;
      });
    });

    return () => { cancelled = true; };
  }, [audit]);

  const totalTokens = audit.reduce((n, r) => n + (r.total_tokens || 0), 0);
  const avgElapsed = audit.length
    ? Math.round(audit.reduce((n, r) => n + (r.elapsed_ms || 0), 0) / audit.length)
    : 0;
  const needsReview = audit.filter(
    (r) => r.status === 'needs_attention' || r.status === 'insufficient_evidence',
  ).length;
  const failures = audit.filter((r) => r.status === 'error' || r.status === 'timeout').length;
  const studentGroups = groupAuditHierarchy(audit);

  const toggleStudent = (key) => {
    setExpandedStudents((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFile = (key) => {
    setExpandedFiles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await clearPipelineRecords();
      setExpandedStudents({});
      setExpandedFiles({});
      setStudentLabels({});
      await load();
      setConfirmClear(false);
      setErr('');
    } catch {
      setErr('Failed to clear pipeline records.');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-3 sm:p-6">
      <ConfirmClearModal
        open={confirmClear}
        busy={clearing}
        onCancel={() => setConfirmClear(false)}
        onConfirm={handleClear}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-5">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold text-slate-100">Pipeline Monitor</h1>
          <p className="text-xs text-slate-500 break-all">
            {user?.email} · {updatedAt ? `updated ${formatClockTime(updatedAt)}` : 'loading…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setLive((v) => !v)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${live ? 'bg-emerald-600/20 border-emerald-600 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-300'}`}
          >
            {live ? '● Live' : 'Paused'}
          </button>
          <button type="button" onClick={load} className="px-3 py-1.5 rounded-lg text-sm bg-slate-800 border border-slate-700">
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setConfirmClear(true)}
            className="px-3 py-1.5 rounded-lg text-sm bg-amber-600/20 border border-amber-700 text-amber-300"
          >
            Clear records
          </button>
          <button
            type="button"
            onClick={() => { sessionStorage.removeItem('backdoor_ok'); logout(); }}
            className="px-3 py-1.5 rounded-lg text-sm bg-red-600/20 border border-red-700 text-red-300"
          >
            Sign out
          </button>
        </div>
      </div>

      {err && <div className="mb-4 text-sm text-red-400">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <Stat label="Running" value={status?.running ?? '—'} tone="green" />
        <Stat label="Queued" value={status?.queued ?? '—'} tone="amber" />
        <Stat
          label="GPU slots"
          value={status ? `${status.gpu_slots_in_use ?? 0}/${status.max_gpu_slots ?? '—'}` : '—'}
          tone="indigo"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <Stat label="Students" value={studentGroups.length} />
        <Stat label="Total tokens" value={totalTokens.toLocaleString()} tone="indigo" />
        <Stat label="Avg section time" value={formatDurationMs(avgElapsed)} />
        <Stat label="Needs review / fails" value={`${needsReview} / ${failures}`} tone={failures ? 'red' : 'amber'} />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <div className="px-3 sm:px-4 py-3 border-b border-slate-800 text-sm font-medium text-slate-300">
          Recent section audit
        </div>
        <AuditTableDesktop
          studentGroups={studentGroups}
          studentLabels={studentLabels}
          expandedStudents={expandedStudents}
          expandedFiles={expandedFiles}
          onToggleStudent={toggleStudent}
          onToggleFile={toggleFile}
        />
        <AuditCardsMobile
          studentGroups={studentGroups}
          studentLabels={studentLabels}
          expandedStudents={expandedStudents}
          expandedFiles={expandedFiles}
          onToggleStudent={toggleStudent}
          onToggleFile={toggleFile}
        />
      </div>

      <p className="text-xs text-slate-600 mt-4">
        Live buffer: {traces.length} traces · Auto-refresh every {REFRESH_MS / 1000}s
      </p>
    </div>
  );
}

/* ------------------------------- Page --------------------------------- */
export default function BackdoorPage() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('backdoor_ok') === '1');
  if (!unlocked) return <BackdoorGate onUnlock={() => setUnlocked(true)} />;
  return <BackdoorDashboard />;
}
